import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './Auth.css';

const Login = ({ onSwitchToSignup, onSwitchToReset }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [authView, setAuthView] = useState('landing'); // 'landing', 'login', 'signup', 'reset'
  const { login, signup, resetPassword } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setMessage('');
      setLoading(true);
      await login(email, password);
    } catch (error) {
      setError('Failed to log in. Please check your credentials.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    try {
      setError('');
      setMessage('');
      setLoading(true);
      await signup(email, password);
    } catch (error) {
      setError('Failed to create an account. Email may already be in use.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();

    try {
      setMessage('');
      setError('');
      setLoading(true);
      await resetPassword(email);
      setMessage('Check your inbox for password reset instructions. If you don\'t see it, check your spam folder.');
    } catch (error) {
      setError('Failed to reset password. Please check your email address.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const switchView = (view) => {
    setAuthView(view);
    setError('');
    setMessage('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="landing-page">
      {/* Consistent Header like NetflixUI */}
      <header className="netflix-header">
        <div className="logo">
          <h1>Vision Games</h1>
          <p>Play Games by Moving Your Body</p>
        </div>
      </header>

      {/* Hero Section with Auth Forms */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          {authView === 'landing' && (
            <>
              <h1 className="hero-title">Your Body is the Controller</h1>
              <p className="hero-subtitle">
                Play interactive games without any controllers. Just you, your camera, and endless fun.
              </p>
              <div className="hero-buttons">
                <button className="cta-button primary" onClick={() => switchView('login')}>
                  Get Started
                </button>
              </div>
            </>
          )}

          {/* Login Form */}
          {authView === 'login' && (
            <div className="auth-form-inline">
              <h2>Welcome Back</h2>
              <p className="auth-subtitle">Sign in to start playing</p>

              {error && <div className="auth-error">{error}</div>}
              
              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="auth-button"
                  disabled={loading}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>

              <div className="auth-links">
                <button onClick={() => switchView('reset')} className="link-button">
                  Forgot password?
                </button>
              </div>

              <div className="auth-footer">
                <p>New to VisionGames?</p>
                <button onClick={() => switchView('signup')} className="signup-link">
                  Create an account
                </button>
              </div>

              <button onClick={() => switchView('landing')} className="back-button">
                ← Back
              </button>
            </div>
          )}

          {/* Signup Form */}
          {authView === 'signup' && (
            <div className="auth-form-inline">
              <h2>Create Your Account</h2>
              <p className="auth-subtitle">Start your VisionGames journey</p>

              {error && <div className="auth-error">{error}</div>}
              
              <form onSubmit={handleSignup}>
                <div className="form-group">
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <input
                    type="password"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="auth-button"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>

              <div className="auth-footer">
                <p>Already have an account?</p>
                <button onClick={() => switchView('login')} className="signup-link">
                  Sign in
                </button>
              </div>

              <button onClick={() => switchView('landing')} className="back-button">
                ← Back
              </button>
            </div>
          )}

          {/* Reset Password Form */}
          {authView === 'reset' && (
            <div className="auth-form-inline">
              <h2>Reset Password</h2>
              <p className="auth-subtitle">Enter your email to receive reset instructions</p>

              {error && <div className="auth-error">{error}</div>}
              {message && <div className="auth-success">{message}</div>}
              
              <form onSubmit={handleReset}>
                <div className="form-group">
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="auth-button"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              <div className="auth-footer">
                <p>Remember your password?</p>
                <button onClick={() => switchView('login')} className="signup-link">
                  Back to Sign In
                </button>
              </div>

              <button onClick={() => switchView('landing')} className="back-button">
                ← Back
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Features Section - Only show on landing view */}
      {authView === 'landing' && (
        <>
          <section className="features-section">
            <div className="feature-card large">
              <div className="feature-content">
                <h2>🕹️ No Controllers. Just Move.</h2>
                <p>
                  Forget joysticks and gamepads. Your body is the only controller you need. 
                  Jump, dodge, punch, and dance your way through our collection of motion-controlled games.
                </p>
              </div>
              <div className="feature-visual">
                <div className="visual-placeholder">🎮</div>
              </div>
            </div>

            <div className="feature-card reverse">
              <div className="feature-content">
                <h2>💪 Fitness Meets Fun</h2>
                <p>
                  Who said gaming has to be sedentary? Get your heart pumping while having a blast. 
                  Burn calories, improve coordination, and stay active - all while playing games you love.
                </p>
              </div>
              <div className="feature-visual">
                <div className="visual-placeholder">💪</div>
              </div>
            </div>

            <div className="feature-card">
              <div className="feature-content">
                <h2>👨‍👩‍👧‍👦 Perfect for Families</h2>
                <p>
                  Create memories with your loved ones. VisionGames brings everyone together for 
                  screen time that's actually active time. Fun for all ages, from kids to grandparents.
                </p>
              </div>
              <div className="feature-visual">
                <div className="visual-placeholder">👨‍👩‍👧‍👦</div>
              </div>
            </div>

            <div className="feature-card reverse">
              <div className="feature-content">
                <h2>📹 Become a Content Creator</h2>
                <p>
                  Record your epic gameplay and share it on YouTube! Build your audience, earn revenue 
                  from ads, and join the VisionGames creator community. Don't forget to tag <strong>#VisionGames.ai</strong>
                </p>
              </div>
              <div className="feature-visual">
                <div className="visual-placeholder">📹</div>
              </div>
            </div>

            <div className="feature-card">
              <div className="feature-content">
                <h2>📱 Play Anywhere</h2>
                <p>
                  iPad, laptop, smart TV with webcam - play on any device with a camera. 
                  Connect your laptop to your TV for the ultimate big-screen experience.
                </p>
              </div>
              <div className="feature-visual">
                <div className="visual-placeholder">📱</div>
              </div>
            </div>
          </section>

          {/* Privacy Section */}
          <section className="privacy-section">
            <div className="privacy-content">
              <h3>🔒 Your Privacy Matters</h3>
              <p>
                All processing happens locally on your device. Your camera feed is never uploaded, 
                stored, or shared. What happens in your living room stays in your living room.
              </p>
            </div>
          </section>

          {/* CTA Section */}
          <section className="cta-section">
            <h2>Ready to Get Moving?</h2>
            <p>Join thousands of players worldwide who are gaming differently.</p>
            <button className="cta-button primary large" onClick={() => switchView('login')}>
              Start Playing Now
            </button>
          </section>
        </>
      )}

      {/* Footer */}
      <footer className="landing-footer">
        <p>&copy; 2026 VisionGames.ai - The Future of Interactive Gaming</p>
      </footer>
    </div>
  );
};

export default Login;